import get from 'lodash/get';

const TTF = 'ttf';
const TRUE_TYPE = 'truetype';
const OTF = 'otf';
const OPEN_TYPE = 'opentype';
const EOT = 'eot';
const EMBEDDED_OPEN_TYPE = 'embedded-opentype';
// ttf font file types must be loaded as trueType to use.
const FORMAT_TO_TYPE = {
    [TTF]: TRUE_TYPE,
    [OTF]: OPEN_TYPE,
    [EOT]: EMBEDDED_OPEN_TYPE,
};

/**
 * @param {array} fonts    Array of objects with a "name" and an optional array of "variants"
 *
 * @return HTMLElement     Returns a link tag with a Google font href
 */
export function getGoogleFontElement(fonts) {
    if (!fonts.length) {
        return null;
    }

    const link = document.createElement('link');
    const fontString = fonts
        .map((font) => `${font.name}${(font.variants || []).length ? `:${font.variants.join(',')}` : ''}`)
        .join('|');

    link.rel = 'stylesheet';
    link.type = 'text/css';
    link.href = `//fonts.googleapis.com/css?family=${fontString}`;

    return link;
}

/**
 * @param {array} fonts    Array of objects with a "name" and an array of "files"
 *
 * @return HTMLElement     Returns a style tag with font-face css inside
 */
export function getCustomFontElement(fonts) {
    if (!fonts.length) {
        return null;
    }

    const style = document.createElement('style');
    const innerHTML = fonts
        .map((font) => {
            /* eslint-disable indent */
            // eslint did NOT like this.
            // I thought it was kinda cool ¯\_(ツ)_/¯
            const files = get(font, 'files', []);
            if (!(files || []).length) {
                return '';
            }
            return `
            @font-face {
                font-family: '${font.name}';
                src: ${font.files
                    .map((file) => {
                        const fileExt = file.split('.').pop();
                        return `url("${file}") format("${FORMAT_TO_TYPE[fileExt] || fileExt}")`;
                    })
                    .join(', ')};
            }
            `;
            /* eslint-enable indent */
        })
        .join('\n');

    style.id = 'font-loader';
    style.type = 'text/css';
    style.innerHTML = innerHTML;

    return style;
}

/**
 * Adds fonts to the page. Either google or custom fonts.
 * ⚠️ Caution: You are responsible for this not running multiple times on a page
 * @param {array} fonts
 */
function fontLoader(fonts) {
    const googleFonts = fonts.filter((font) => font.google);
    const customFonts = fonts.filter((font) => font.custom);

    const googleFontElement = getGoogleFontElement(googleFonts);
    const customFontElement = getCustomFontElement(customFonts);

    if (googleFontElement) {
        document.head.appendChild(googleFontElement);
    }

    if (customFontElement) {
        document.head.appendChild(customFontElement);
    }
}

/**
 * Adds fonts to the page based on an array of objects with a "name" and an array of "files"
 * ⚠️ Caution: You are responsible for this not running multiple times on a page
 * @param {array} fonts
 */
export function customFontLoader(fonts) {
    const customFontElement = getCustomFontElement(fonts);

    if (customFontElement) {
        document.head.appendChild(customFontElement);
    }
}

/**
 * Adds fonts to the page based on an array of objects with a "name" and an array of "files"
 * ⚠️ Caution: You are responsible for this not running multiple times on a page
 * @param {array} fonts
 */
export function googleFontLoader(fonts) {
    const googleFontElement = getGoogleFontElement(fonts);

    if (googleFontElement) {
        document.head.appendChild(googleFontElement);
    }
}

/**
 * Adds branded theme fonts to the page based on an array of objects with a "name" and an array of "files"
 * If you call this function multiple times, it will only load the fonts once. it checks for an existing
 * style tag with the ID 'branded-theme-font-loader'.
 * @param fonts
 */
export function brandedThemeFontsLoader(fonts = []) {
    const styleId = 'branded-theme-font-loader';
    const hasLoaded = document.getElementById(styleId);

    if (!fonts.length || hasLoaded) {
        return;
    }

    const style = getCustomFontElement(fonts);
    if (style && style instanceof HTMLElement) {
        style.id = styleId;
        document.head.appendChild(style);
    }
}

export default fontLoader;
